import React, { useState, useEffect } from 'react';
import { getSchools, createSchool, updateSchool, deleteSchool } from '../services/api';

const EMPTY = { school_id: '', name: '', address: '', telephone: '' };

export default function SchoolsPage() {
  const [schools,  setSchools]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form,     setForm]     = useState(EMPTY);
  const [saving,   setSaving]   = useState(false);
  const [msg,      setMsg]      = useState('');
  const [search,   setSearch]   = useState('');

  const load = () => {
    setLoading(true);
    getSchools()
      .then(res => setSchools(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm(EMPTY);
    setEditItem(null);
    setShowForm(true);
    setMsg('');
  };

  const openEdit = (school) => {
    setForm({
      school_id: school.school_id  || '',
      name:      school.name       || '',
      address:   school.address    || '',
      telephone: school.telephone  || '',
    });
    setEditItem(school);
    setShowForm(true);
    setMsg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.school_id || !form.name || !form.address) {
      setMsg('❌ School ID, නම සහ ලිපිනය අවශ්‍යයි');
      return;
    }
    setSaving(true);
    try {
      if (editItem) {
        await updateSchool(editItem.id, form);
        setMsg('✅ පාසල යාවත්කාලීන කරන ලදී');
      } else {
        await createSchool(form);
        setMsg('✅ පාසල සාර්ථකව එකතු කරන ලදී');
      }
      load();
      setTimeout(() => { setShowForm(false); setMsg(''); }, 1200);
    } catch (err) {
      setMsg('❌ ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`"${name}" මකා දැමීමට අවශ්‍යද?`)) return;
    try {
      await deleteSchool(id);
      load();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.error || err.message));
    }
  };

  const filtered = schools.filter(s =>
    s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.school_id?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">පාසල්</h1>
          <p className="text-gray-500 text-sm mt-1">පාසල් කළමනාකරණය කරන්න</p>
        </div>
        <button onClick={openCreate}
          className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
          </svg>
          පාසල එකතු කරන්න
        </button>
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="පාසල් නම හෝ ID අනුව සොයන්න..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full max-w-sm border border-gray-300 rounded-lg px-4 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
        />
      </div>

      {/* Modal Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            {/* Modal Header */}
            <div className="bg-red-500 rounded-t-2xl px-6 py-4 flex items-center justify-between">
              <h2 className="text-white font-semibold">
                {editItem ? 'පාසල සංස්කරණය' : 'නව පාසල එකතු කරන්න'}
              </h2>
              <button onClick={() => setShowForm(false)} className="text-white/80 hover:text-white text-xl">✕</button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {msg && (
                <div className={`text-sm p-3 rounded-lg ${
                  msg.startsWith('✅') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                }`}>{msg}</div>
              )}

              {[
                { field: 'school_id', label: 'පාසල් ID',        req: true,  type: 'text' },
                { field: 'name',      label: 'පාසල් නම',        req: true,  type: 'text' },
                { field: 'telephone', label: 'දුරකථන අංකය',    req: false, type: 'text' },
              ].map(({ field, label, req, type }) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label}{req && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <input
                    type={type}
                    value={form[field]}
                    onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
                  />
                </div>
              ))}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ලිපිනය <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={3}
                  value={form.address}
                  onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50 transition">
                  අවලංගු
                </button>
                <button type="submit" disabled={saving}
                  className="flex-1 bg-red-500 text-white py-2 rounded-lg text-sm hover:bg-red-600 disabled:opacity-50 transition">
                  {saving ? 'සුරකිමින්...' : 'සුරකින්න'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="text-center py-16 text-gray-400">පූරණය වෙමින්...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <p className="text-4xl mb-3">🏫</p>
            <p className="text-sm">{search ? 'ගැලපෙන පාසල් නොමැත' : 'පාසල් නොමැත'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['#', 'පාසල් ID', 'නම', 'දුරකථන', 'ලිපිනය', 'ක්‍රියා'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((school, i) => (
                  <tr key={school.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-400">{i + 1}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{school.school_id}</td>
                    <td className="px-4 py-3 text-gray-800">{school.name}</td>
                    <td className="px-4 py-3 text-gray-600">{school.telephone || '-'}</td>
                    <td className="px-4 py-3 text-gray-500 max-w-xs truncate">{school.address}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-3">
                        <button onClick={() => openEdit(school)}
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium">
                          සංස්කරණය
                        </button>
                        <button onClick={() => handleDelete(school.id, school.name)}
                          className="text-red-500 hover:text-red-700 text-xs font-medium">
                          මකන්න
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-4 py-3 border-t border-gray-100 text-xs text-gray-400">
              මුළු පාසල්: {filtered.length}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}